import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Calendar, MapPin, User, Building } from 'lucide-react';

interface ScanResultProps {
  result: {
    success: boolean;
    already_checked_in: boolean;
    message: string;
    registration: {
      id: number;
      event_title: string;
      event_date_time: string;
      event_venue: string;
      attendee_name: string;
      attendee_email: string;
      attendee_company: string;
      status: string;
      checked_in_at: string | null;
      dietary_requirements?: string;
      accessibility_requirements?: string;
    };
  } | null;
  onClose: () => void;
  onScanNext: () => void;
}

export const ScanResult: React.FC<ScanResultProps> = ({ result, onClose, onScanNext }) => {
  if (!result) return null;

  const { success, already_checked_in, message, registration } = result;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        {/* Status Icon and Message */}
        <div className="text-center mb-6">
          {success ? (
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-3" />
          ) : already_checked_in ? (
            <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-3" />
          ) : (
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-3" />
          )}
          <h2 className={`text-xl font-semibold ${
            success ? 'text-green-700' : already_checked_in ? 'text-orange-700' : 'text-red-700'
          }`}>
            {message}
          </h2>
        </div>

        {/* Attendee Information */}
        <div className="space-y-3 mb-6">
          <div className="flex items-start">
            <User className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">{registration.attendee_name}</p>
              <p className="text-sm text-gray-600">{registration.attendee_email}</p>
            </div>
          </div>

          {registration.attendee_company && (
            <div className="flex items-center">
              <Building className="h-5 w-5 text-gray-400 mr-2" />
              <p className="text-gray-700">{registration.attendee_company}</p>
            </div>
          )}

          <div className="flex items-start">
            <Calendar className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">{registration.event_title}</p>
              <p className="text-sm text-gray-600">
                {new Date(registration.event_date_time).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
            <p className="text-gray-700">{registration.event_venue}</p>
          </div>

          {registration.checked_in_at && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Checked in at: {new Date(registration.checked_in_at).toLocaleTimeString()}
              </p>
            </div>
          )}

          {(registration.dietary_requirements || registration.accessibility_requirements) && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-1">Special Requirements:</p>
              {registration.dietary_requirements && (
                <p className="text-sm text-blue-700">Dietary: {registration.dietary_requirements}</p>
              )}
              {registration.accessibility_requirements && (
                <p className="text-sm text-blue-700">Accessibility: {registration.accessibility_requirements}</p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onScanNext}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Scan Next
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};